import { useInAudit } from 'src/context/AuditContext'
import {
  Dashboard,
  DASHBOARD_CONTEXT,
  Filter,
  Filters,
  GetDashboardFilterValuesRequest,
  GetDashboardFilterValuesResponse,
  GetDashboardLayoutRequest,
  GetDashboardLayoutResponse,
  GetDashboardLiveCountsRequest,
  GetDashboardRequest,
  GetReportingChildAccountsResponse,
  LiveChartCounts as LiveChartCountsProto,
  SaveDashboardLayoutRequest,
} from '@trustero/trustero-api-web/lib/dashboard/dashboard_pb'
import { DashboardServicePromiseClient } from '@trustero/trustero-api-web/lib/dashboard/dashboard_grpc_web_pb'
import { useLocation } from 'react-router-dom'
import { useCallback, useMemo } from 'react'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import queryString, { ParsedQuery } from 'query-string'
import { DashboardDateRangeDefaults } from 'src/components/Reusable/IndexPage/FilterBar/FilterDropdowns/DashboardFilterDropdowns/DashboardDateRangeFilterDropdown/DashboardDateRangeFilterDropdown.types'
import { TimeRangeConfig } from 'src/components/Reusable/Charts/Common/Charts.types'
import {
  DashboardGroupBy,
  DashboardType,
  DashboardViewParam,
  LiveChartCounts,
} from 'src/Utils/dashboards/dashboards.types'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { useCurrentAudit } from 'src/components/async/model/audit'
import { useHasFeatureAccess } from 'src/context/Gatekeeper'
import { FEATURE_ACCESS } from '@trustero/trustero-api-web/lib/account/account_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import {
  GrpcResponse,
  NewGrpcResponse,
} from '../../components/async/hooks/types'
import { useSwrImmutableGrpc } from '../../components/async/useSwrImmutableGrpc'
import { useSwrGrpc } from '../../components/async/useSwrGrpc'
import { daysToMillis } from '../globalHelpers'
import {
  applyDashboardFilters,
  getDashboardStepFromTimeBucket,
  getIsDashboardAuditSpecific,
} from './dashboards.helpers'
import { getSections, Section } from './dashboards.utils'

export const useGetTrustPortalDashboard = ({
  dname,
  filters,
  shouldFetch = false,
}: {
  dname: string
  filters: Filters
  shouldFetch?: boolean
}): GrpcResponse<Dashboard> => {
  return useGetDashboard({
    dname,
    filters,
    context: DASHBOARD_CONTEXT.TRUST_PORTAL,
    shouldFetch,
  })
}

export const useGetDashboard = ({
  dname,
  filters,
  context,
  shouldFetch = false,
}: {
  dname: string
  filters: Filters
  context?: DASHBOARD_CONTEXT
  shouldFetch?: boolean
}): GrpcResponse<Dashboard> => {
  const hasName = !!(dname && dname.length)
  const { auditId } = useInAudit()
  const request = new GetDashboardRequest()
    .setName(dname || '')
    .setIncludeData(true)
    .setFilters(filters)
  if (context) {
    request.setDashboardContext(context)
  }
  if (getIsDashboardAuditSpecific(dname)) {
    request.setAuditId(auditId || '')
  }
  const { response } = useSwrImmutableGrpc(
    DashboardServicePromiseClient.prototype.getDashboard,
    request,
    shouldFetch && hasName,
    { keepPreviousData: true },
  )

  return NewGrpcResponse(response)
}

export const useGetDashboardLiveCounts = ({
  dname,
  filters,
  chartNames,
  shouldFetch = false,
}: {
  dname: string
  filters: Filters
  chartNames?: string[]
  shouldFetch?: boolean
}): {
  data: Map<string, LiveChartCounts> | undefined
  loading: boolean
  isValidating: boolean
} => {
  const { auditId } = useInAudit()
  const request = new GetDashboardLiveCountsRequest()
    .setDashboardName(dname)
    .setFilters(filters)
  if (auditId) {
    request.setAuditId(auditId)
  }
  if (chartNames?.length) {
    request.setChartNamesList(chartNames)
  }
  const { response } = useSwrGrpc(
    DashboardServicePromiseClient.prototype.getDashboardLiveCounts,
    request,
    shouldFetch && !!(dname && dname.length),
    { keepPreviousData: true },
  )

  const data = useMemo(() => {
    if (!response.data) return undefined
    const result = new Map<string, LiveChartCounts>()
    response.data
      .getCountsMap()
      .forEach((counts: LiveChartCountsProto, chartName: string) => {
        const breakdown: Record<string, number> = {}
        counts.getBreakdownMap().forEach((count: number, key: string) => {
          breakdown[key] = count
        })
        result.set(chartName, { total: counts.getTotal(), breakdown })
      })
    return result
  }, [response.data])

  return {
    data,
    loading: !response.data && !response.error,
    isValidating: response.isValidating ?? false,
  }
}

export function useDashboardFilter(defaultDays = 31): Filters {
  const location = useLocation()
  const filters = useMemo(() => {
    const end = 0
    const start = Date.now() - 1000 * 60 * 60 * 24 * defaultDays
    const filters = new Filters() // Apply all filters for each parameter in URL
    filters.setStarttime(start)
    filters.setEndtime(end)
    const queryParams: ParsedQuery<string> = queryString.parse(
      location.search,
      {
        arrayFormat: 'bracket',
      },
    )
    Object.values(FilterParam).forEach((filterType: FilterParam) =>
      applyDashboardFilters(filters, queryParams, filterType),
    )
    return filters
  }, [location.search, defaultDays])
  return filters
}

// useOEDashboardSections returns the section list to render for an OE
// dashboard, filtered by the active filter-bar selections.
//
// - Framework mode (default): drops sections not in the dashboard_framework URL
//   param (when set) and not in the current audit's framework list.
// - Labels mode: drops sections whose combo doesn't contain any of the
//   dashboard_label URL-param values (when set); ignores audit-framework
//   scoping since label-combo sections aren't framework-named.
// - Account mode: passes sections through; the account superfilter has already
//   decided which accounts were charted.
export const useOEDashboardSections = (
  dashboard: Dashboard | undefined,
  groupBy: DashboardGroupBy = DashboardGroupBy.FRAMEWORK,
): Section[] => {
  const location = useLocation()
  const { data: audit } = useCurrentAudit()
  const sections = useDashboardSections(dashboard)
  return useMemo(() => {
    if (!dashboard) {
      return []
    }
    const queryParams: ParsedQuery<string> = queryString.parse(
      location.search,
      { arrayFormat: 'bracket' },
    )

    if (groupBy === DashboardGroupBy.ACCOUNT) {
      return [...sections]
    }

    if (groupBy === DashboardGroupBy.LABELS) {
      const labelFilter = queryParams[FilterParam.DASHBOARD_LABEL as string] as
        | string[]
        | undefined
      if (!labelFilter?.length) {
        return [...sections]
      }
      return sections.filter((section) =>
        section.getFilterValues().some((label) => labelFilter.includes(label)),
      )
    }

    const frameworkFilter = queryParams[
      FilterParam.DASHBOARD_FRAMEWORK as string
    ] as string[] | undefined
    const auditFrameworkNames =
      audit
        ?.getComplianceFrameworksList()
        .map((framework: ComplianceFramework) => framework.getName()) || []
    let filteredSections = [...sections]
    if (frameworkFilter?.length) {
      filteredSections = filteredSections.filter((section) =>
        frameworkFilter.includes(section.getName()),
      )
    }
    if (auditFrameworkNames.length) {
      filteredSections = filteredSections.filter((section) =>
        auditFrameworkNames.includes(section.getName()),
      )
    }
    return filteredSections
  }, [dashboard, sections, groupBy, location.search, audit])
}

export const useDashboardSections = (dashboard?: Dashboard): Section[] => {
  return useMemo(() => {
    if (!dashboard) {
      return []
    }
    return getSections(dashboard)
  }, [dashboard])
}

export const useGetURLParams = (): ParsedQuery<string> => {
  const location = useLocation()
  return queryString.parse(location.search)
}

export const useGetURLParam = <T>(
  name: string,
  validOptions: Set<T>,
  defaultValue: T,
): T => {
  const params = useGetURLParams()
  if (!params[name]) {
    return defaultValue
  }
  const param = Array.isArray(params[name])
    ? (params[name] as Array<unknown>)[0]
    : params[name]
  if (validOptions.has(param as T)) {
    return param as T
  }
  return defaultValue
}

// The dim mirroring source_account_id, and the sentinel the backend maps to "no such dim".
export const CHILD_ACCOUNT_DIM = 'child_account_id'
const OWN_ACCOUNT_FILTER_VALUE = 'none'

const VALID_DASHBOARD_GROUP_BY = new Set<DashboardGroupBy>([
  DashboardGroupBy.FRAMEWORK,
  DashboardGroupBy.LABELS,
  DashboardGroupBy.ACCOUNT,
])

/**
 * Whether this account may group and filter dashboards by reporting child account.
 *
 * Both gates are required and neither implies the other: the account must hold the feature grant,
 * and the user must hold CAN_VIEW_CHILD_ACCOUNT_DASHBOARDS, since a child list names other
 * companies and is not for every dashboard viewer.
 *
 * Also false inside an audit. Audits are per-account, so "the audit" has no cross-account meaning;
 * the dashboard stays parent-only and audit-scoped exactly as today rather than silently switching
 * to continuous-compliance data.
 *
 * `isResolved` matters because the grant loads asynchronously. Callers that fetch a dashboard must
 * wait for it, or a parent account fetches the framework-grouped dashboard first and visibly flips.
 */
export const useCanGroupByAccount = (): {
  canGroupByAccount: boolean
  isResolved: boolean
} => {
  const inAudit = useInAudit()
  const { data: featureAccess, loading } = useHasFeatureAccess(
    FEATURE_ACCESS.CHILD_ACCOUNT_REPORTING,
    !inAudit,
  )
  const hasCapability = useCanPerformAction(
    CAPABILITY.CAN_VIEW_CHILD_ACCOUNT_DASHBOARDS,
  )

  return useMemo(() => {
    if (inAudit) return { canGroupByAccount: false, isResolved: true }
    return {
      canGroupByAccount:
        Boolean(featureAccess?.getAllowAccess()) && hasCapability,
      isResolved: !loading,
    }
  }, [inAudit, featureAccess, hasCapability, loading])
}

/**
 * The grouping in force. Resolves an absent URL param per account type — ACCOUNT in a parent
 * account, FRAMEWORK elsewhere — in this one hook, so the frontend and the URL cannot disagree
 * about what an unparameterized link means.
 *
 * `isResolved` is false until the parent-account check settles; a caller that fetches on the
 * result should hold off until then.
 */
export const useDashboardGroupByState = (): {
  groupBy: DashboardGroupBy
  isResolved: boolean
} => {
  const { canGroupByAccount, isResolved } = useCanGroupByAccount()
  const fromUrl = useGetURLParam<DashboardGroupBy | undefined>(
    DashboardViewParam.GROUP_BY,
    VALID_DASHBOARD_GROUP_BY,
    undefined,
  )

  return useMemo(() => {
    if (fromUrl) {
      // ACCOUNT is meaningless without the entitlement, so a hand-edited or stale link falls back
      // rather than requesting a dashboard the backend will not group.
      if (fromUrl === DashboardGroupBy.ACCOUNT && !canGroupByAccount) {
        return { groupBy: DashboardGroupBy.FRAMEWORK, isResolved }
      }
      return { groupBy: fromUrl, isResolved }
    }
    return {
      groupBy: canGroupByAccount
        ? DashboardGroupBy.ACCOUNT
        : DashboardGroupBy.FRAMEWORK,
      isResolved,
    }
  }, [fromUrl, canGroupByAccount, isResolved])
}

export const useDashboardGroupBy = (): DashboardGroupBy =>
  useDashboardGroupByState().groupBy

// Which template backs each OE tab per grouping. An exhaustive Record so a new grouping cannot be
// added without deciding both names — the compiler asks, rather than one tab silently falling back.
const OE_DASHBOARD_NAMES: Record<
  DashboardGroupBy,
  { overview: DashboardType; detail: DashboardType }
> = {
  [DashboardGroupBy.FRAMEWORK]: {
    overview: DashboardType.OE_OVERVIEW,
    detail: DashboardType.OE_DETAIL,
  },
  [DashboardGroupBy.LABELS]: {
    overview: DashboardType.OE_OVERVIEW_BY_LABEL,
    detail: DashboardType.OE_DETAIL_BY_LABEL,
  },
  [DashboardGroupBy.ACCOUNT]: {
    overview: DashboardType.OE_OVERVIEW_BY_ACCOUNT,
    detail: DashboardType.OE_DETAIL_BY_ACCOUNT,
  },
}

/**
 * The dashboard name for an OE tab under the grouping in force.
 *
 * Hold off fetching until `isResolved`: the parent-account check is asynchronous, so fetching
 * earlier requests the framework-grouped dashboard and then discards it when the grouping flips.
 */
export const useOEDashboardName = (
  variant: 'overview' | 'detail',
): { dname: DashboardType; groupBy: DashboardGroupBy; isResolved: boolean } => {
  const { groupBy, isResolved } = useDashboardGroupByState()
  return useMemo(
    () => ({
      dname: OE_DASHBOARD_NAMES[groupBy][variant],
      groupBy,
      isResolved,
    }),
    [groupBy, isResolved, variant],
  )
}

export const useTimeRangeConfigFromFilters = (
  timeBucket: string,
): TimeRangeConfig => {
  const location = useLocation()
  const queryParams: ParsedQuery<string> = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const filterParams = queryParams[FilterParam.DASHBOARD_DATE_RANGE]
  // use UTC time
  const today = new Date()
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  let minTimeStamp = now - daysToMillis(30)
  let maxTimeStamp = now
  if (filterParams && filterParams.length > 0) {
    switch (filterParams[0]) {
      case DashboardDateRangeDefaults.LAST_7_DAYS:
        minTimeStamp = now - daysToMillis(7)
        break
      case DashboardDateRangeDefaults.LAST_30_DAYS:
        minTimeStamp = now - daysToMillis(30)
        break
      case DashboardDateRangeDefaults.LAST_90_DAYS:
        minTimeStamp = now - daysToMillis(90)
        break
      case DashboardDateRangeDefaults.LAST_YEAR:
        minTimeStamp = now - daysToMillis(365)
        break
      default: {
        const dates = filterParams[0].split('-')
        const startMillis = parseInt(dates[0], 10)
        const endMillis = parseInt(dates[1], 10)
        minTimeStamp = startMillis
        maxTimeStamp = endMillis
        break
      }
    }
  }
  const step = useMemo(
    () => getDashboardStepFromTimeBucket(timeBucket),
    [timeBucket],
  )
  return useMemo(
    () => ({
      start: minTimeStamp,
      end: maxTimeStamp,
      step: step,
    }),
    [minTimeStamp, maxTimeStamp, step],
  )
}

/**
 * Distinct values of one point dimension, scoped to the accounts the superfilter has in view.
 *
 * The selection is part of the request, so it is also part of the SWR cache key — switching
 * accounts refetches rather than serving the previous selection's values. It is sent even for
 * ordinary accounts, where it is empty and the server reads their own points as before.
 */
export const useDashboardFilterValues = (
  dashboardContext: DASHBOARD_CONTEXT,
  dashboardName: string,
  dimension: string,
): GrpcResponse<GetDashboardFilterValuesResponse> => {
  const selectedChildren = useSelectedChildAccounts()
  const { canGroupByAccount } = useCanGroupByAccount()
  // Audit-scoped so the values on offer match the charts. Inside an audit the dashboard shows only
  // that audit's points, and a filter listing values from outside it would narrow every chart to
  // nothing when selected.
  const { auditId } = useInAudit()
  const request = useMemo(
    () =>
      new GetDashboardFilterValuesRequest()
        .setDashboardContext(dashboardContext)
        .setDashboardName(dashboardName)
        .setDimension(dimension)
        .setChildAccountIdsList(canGroupByAccount ? selectedChildren : [])
        .setAuditId(auditId ?? ''),
    [
      dashboardContext,
      dashboardName,
      dimension,
      canGroupByAccount,
      selectedChildren,
      auditId,
    ],
  )
  const { response } = useSwrImmutableGrpc(
    DashboardServicePromiseClient.prototype.getDashboardFilterValues,
    request,
    true,
  )
  return NewGrpcResponse(response)
}

/**
 * Expands JSON-array dimension values into their elements. Array-valued dimensions such as
 * frameworks are stored as ["SOC 2","ISO 27001"], and a dropdown needs the individual names.
 */
export const flattenDimensionValues = (raw: string[]): string[] => {
  const values = new Set<string>()
  raw.forEach((rawValue) => {
    const trimmed = rawValue.trim()
    if (!trimmed) return
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          parsed.forEach((item) => {
            const value = String(item).trim()
            if (value) values.add(value)
          })
          return
        }
      } catch {
        // Not JSON after all — keep the raw text rather than dropping the value.
      }
    }
    values.add(trimmed)
  })
  return Array.from(values)
}

export const useGetDashboardLayout = (
  dashboardName: string,
): GrpcResponse<GetDashboardLayoutResponse> => {
  const { response } = useSwrImmutableGrpc(
    DashboardServicePromiseClient.prototype.getDashboardLayout,
    new GetDashboardLayoutRequest().setDashboardName(dashboardName),
    !!dashboardName,
  )
  return NewGrpcResponse(response)
}

export const useSaveDashboardLayout = (): ((
  dashboardName: string,
  layoutJson: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(DashboardServicePromiseClient)
  return useCallback(
    async (dashboardName: string, layoutJson: string) => {
      const req = new SaveDashboardLayoutRequest()
        .setDashboardName(dashboardName)
        .setLayoutJson(layoutJson)
      await client.saveDashboardLayout(req)
    },
    [client],
  )
}

/**
 * The child accounts whose data this account may currently show — linked, consented, and sharing at
 * least one control. Empty for almost every account, which is the signal to hide the superfilter row
 * rather than render a picker holding only the caller.
 *
 * Not fetched unless the account can group by account, since the RPC requires
 * CAN_VIEW_CHILD_ACCOUNT_DASHBOARDS and every other caller would earn a permission-denied.
 */
export const useReportingChildAccounts =
  (): GrpcResponse<GetReportingChildAccountsResponse> => {
    const { canGroupByAccount } = useCanGroupByAccount()
    const { response } = useSwrImmutableGrpc(
      DashboardServicePromiseClient.prototype.getReportingChildAccounts,
      new Empty(),
      canGroupByAccount,
    )
    return NewGrpcResponse(response)
  }

/**
 * The child accounts currently selected in the superfilter, as they should reach the server.
 *
 * `ownAccountValue` is the sentinel the backend already understands: a filter value of "none"
 * becomes `(NOT key:* OR key:"")`, which is exactly "points carrying no child_account_id" — the
 * viewing account's own data. It must be sent explicitly, because an ABSENT filter means no
 * narrowing at all, which would silently mix every child into a parent's own numbers. The URL param
 * is omitted in the default state; the filter never is.
 */
export const useSelectedChildAccounts = (): string[] => {
  const location = useLocation()
  return useMemo(() => {
    const parsed = queryString.parse(location.search, {
      arrayFormat: 'bracket',
    })
    const raw = parsed[DashboardViewParam.ACCOUNTS as string]
    if (!raw) return []
    return Array.isArray(raw) ? (raw.filter(Boolean) as string[]) : [raw]
  }, [location.search])
}

/**
 * The OE dashboards' filter set: the shared dashboard filters plus the account superfilter scope.
 *
 * Separate from useDashboardFilter so the account scope reaches only these two dashboards. Adding it
 * there would attach a child_account_id clause to every dashboard in the product — harmless in
 * meaning for accounts with no aggregated data, but it would still change every cache key.
 */
export const useOEDashboardFilter = (): Filters => {
  const filters = useDashboardFilter()
  const { canGroupByAccount } = useCanGroupByAccount()
  const selected = useSelectedChildAccounts()

  return useMemo(() => {
    if (!canGroupByAccount) return filters
    // Own account is always included: the superfilter adds children to the parent's own view rather
    // than replacing it.
    const values = [OWN_ACCOUNT_FILTER_VALUE, ...selected]
    const scoped = filters.clone()
    scoped.addFilters(
      new Filter().setKey(CHILD_ACCOUNT_DIM).setValuesList(values),
    )
    return scoped
  }, [filters, canGroupByAccount, selected])
}
